import {Editor} from '@tiptap/react';
import React, {useCallback, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useSelector} from 'react-redux';
import {slideScaleSelector} from '../../../../store/selectors/slide.selectors';
import TextStyleToolbar from './TextStyleToolbar';
import {hasTextRangeSelection} from './textStyleCommands';

const TOOLBAR_GAP_PX = 8;
const VIEWPORT_PADDING_PX = 8;

function getToolbarAnchorRect(editor: Editor): DOMRect {
    const editorDom = editor.view.dom;
    const wrapper = editorDom.closest('.c-textElement, .c-tableElementV2, .c-richTextPart') ?? editorDom.parentElement ?? editorDom;
    return wrapper.getBoundingClientRect();
}

interface TextSelectionFloatingToolbarProps {
    editor: Editor | null;
}

function TextSelectionFloatingToolbar({editor}: TextSelectionFloatingToolbarProps): JSX.Element | null {
    const toolbarRef = useRef<HTMLDivElement>(null);
    const slideScale = useSelector(slideScaleSelector) || 1;
    const [position, setPosition] = useState<{top: number; left: number; placeBelow: boolean} | null>(null);

    const updatePosition = useCallback((): void => {
        if (!editor || editor.isDestroyed || !editor.isEditable || !editor.isFocused || !hasTextRangeSelection(editor)) {
            setPosition(null);
            return;
        }

        const rect = getToolbarAnchorRect(editor);
        const effectiveScale = Math.max(1, slideScale);
        const gap = TOOLBAR_GAP_PX * effectiveScale;
        const toolbarHeight = (toolbarRef.current?.offsetHeight ?? 40) * effectiveScale;
        const placeBelow = rect.top - toolbarHeight - gap < VIEWPORT_PADDING_PX;
        const next = {
            left: rect.left + rect.width / 2,
            top: placeBelow ? rect.bottom + gap : rect.top - gap,
            placeBelow,
        };

        setPosition((prev) => {
            if (prev && prev.left === next.left && prev.top === next.top && prev.placeBelow === next.placeBelow) {
                return prev;
            }
            return next;
        });
    }, [editor, slideScale]);

    useLayoutEffect(() => {
        if (!editor) return undefined;

        updatePosition();
        editor.on('selectionUpdate', updatePosition);
        editor.on('transaction', updatePosition);
        editor.on('focus', updatePosition);
        editor.on('blur', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        return () => {
            editor.off('selectionUpdate', updatePosition);
            editor.off('transaction', updatePosition);
            editor.off('focus', updatePosition);
            editor.off('blur', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [editor, updatePosition]);

    const isVisible = position !== null;

    useLayoutEffect(() => {
        if (!isVisible) return undefined;

        let frame = 0;
        const followAnchor = (): void => {
            updatePosition();
            frame = requestAnimationFrame(followAnchor);
        };
        frame = requestAnimationFrame(followAnchor);

        return () => cancelAnimationFrame(frame);
    }, [isVisible, updatePosition]);

    if (!editor || !position) return null;

    return createPortal(
        <div
            ref={toolbarRef}
            className="c-textStyleToolbar--floating"
            style={{
                top: position.top,
                left: position.left,
                transform: `${position.placeBelow ? 'translate(-50%, 0)' : 'translate(-50%, -100%)'} scale(${Math.max(1, slideScale)})`,
                transformOrigin: position.placeBelow ? 'top center' : 'bottom center',
            }}
            data-testid="text-selection-floating-toolbar"
        >
            <TextStyleToolbar editor={editor} compact />
        </div>,
        document.body
    );
}

export default TextSelectionFloatingToolbar;
